import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type VehicleListing } from '@/services/vehicles/types';
import { formatUgx, formatUsd } from '@/utils/vehicleFormat';

type Props = { vehicle: Pick<VehicleListing, 'price' | 'estimates'>; size?: 'card' | 'detail' };

/** Price as recorded: USD and/or UGX, never converted (A8). */
export function VehiclePrice({ vehicle, size = 'card' }: Props) {
  const { price, estimates } = vehicle;
  const main =
    price.usd !== null ? formatUsd(price.usd) : price.ugx !== null ? formatUgx(price.ugx) : null;
  // On a card the UGX equivalent is dropped: it appears on only some cars, so
  // it is the other thing that used to make one card in a row taller than its
  // neighbours. The detail page, which has one car and all the room, shows it.
  const secondary =
    size === 'detail' && price.usd !== null && price.ugx !== null ? formatUgx(price.ugx) : null;
  const terms =
    price.terms === 'cif'
      ? `CIF${price.cifDestination ? ` ${price.cifDestination}` : ''}`
      : price.terms === 'fob'
        ? 'FOB'
        : null;
  const big = size === 'detail';

  return (
    <Stack spacing={0.5} className="tabular-nums" sx={{ minWidth: 0 }}>
      {main ? (
        <Typography
          component="p"
          sx={{
            fontFamily: 'h1.fontFamily',
            fontWeight: 800,
            fontSize: big ? '2rem' : '1.25rem',
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
          }}
        >
          {main}
          {terms && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1.5, fontWeight: 600, letterSpacing: 0 }}
            >
              {terms}
            </Typography>
          )}
        </Typography>
      ) : (
        <Typography component="p" sx={{ fontWeight: 700, fontSize: big ? '1.5rem' : '1.125rem' }}>
          Price on request
        </Typography>
      )}
      {secondary && (
        <Typography variant="body2" color="text.secondary">
          {secondary}
        </Typography>
      )}
      <LandedCost amountUgx={estimates.landedCostUgx} big={big} />
    </Stack>
  );
}

/**
 * The landed-cost estimate, or a placeholder of the same height when there is
 * none.
 *
 * Most listings carry the estimate and some do not, and on a card that
 * difference used to shorten one card in a row and knock every price and
 * button in that row out of alignment. Reserving the line costs nothing and
 * is honest: a buyer who sees "on request" knows the figure exists and that
 * we have not worked it out for this car yet.
 */
function LandedCost({ amountUgx, big }: { amountUgx: number | null; big: boolean }) {
  if (amountUgx === null) {
    return (
      <Typography variant="body2" color="text.disabled" noWrap>
        Landed cost on request
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary" noWrap>
      {big ? 'Est. landed in Uganda: ' : 'Est. landed: '}
      <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {formatUgx(amountUgx)}
      </Typography>
    </Typography>
  );
}
